import { PreviewsType } from 'screens/editorial/asset/context/SyncedStores/UseNxtSyncStore';
import { isFiniteNumber } from 'utility/functions';

export type CropData = { left?: number; top?: number; width?: number; height?: number };
export type CropValues = CropData | false | null;

// required type for some math ops needing all values
type ReqCropObj = Required<CropData>;

// Validate crop data object with all required number values
export const getIsValidCropObj = (value?: CropValues): value is ReqCropObj => {
  const check =
    Boolean(value) &&
    typeof value === 'object' &&
    typeof value?.left === 'number' &&
    typeof value?.top === 'number' &&
    typeof value?.width === 'number' &&
    typeof value?.height === 'number';
  return check;
};

export type ImageData = {
  src?: string;
  height?: number;
  width?: number;
  size?: number;
  type?: string;
  crop?: CropValues;
  preview?: string; // potentially cropped preview
  add_binary?: string | null; // for image upload
  remove_binary?: boolean; // for image removal
  previews?: PreviewsType; // img previews response...
};

/**
 * Extracts the UUID from a given image url.
 * eg: http://cdn.../79fa35d7-c946-4435-b344-b384cc16f27f/68f6576b51642.image.jpg?resize=750%2C1127
 * result: 79fa35d7-c946-4435-b344-b384cc16f27f/68f6576b51642
 */
export function extractIdFromUrl(url?: string): string | null {
  if (!url) {
    return null;
  }
  const match = url.match(
    /\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/([a-z0-9]+)\.(?:preview|image)\.jpg/i,
  );
  return match ? `${match[1]}/${match[2]}` : null;
}

/**
 * eg: `url.split('?')[0] ?? undefined`
 * Provide eg an image URL and returns the base part without query parameters
 * for getting a clean URL without any crop/resize parameters.
 * Returns original string as-is if no ? delimiter found.
 * Returns empty string if url undefined or no split part found.
 * Else, returns the first part of the split string (clean url).
 */
export function getBaseCleanUrl(url?: string): string | undefined {
  if (!url) {
    return undefined;
  }
  // If no qp found, return string as-is
  const hasQp = url.includes('?');
  if (!hasQp) {
    return url;
  }
  // split and return first part
  const cleaned = url.split('?')[0] ?? undefined;
  return cleaned;
}

/**
 * Helper to normalize blox crop data on file slots
 * for usage in the cropper components and determining
 * if we need to recalculate crop preview in some cases
 *
 * note: as this can return an object, if you use the result
 * of this within a dependency array (eg useEffect/useMemo)
 * you should memoize the result variable with useMemo to avoid
 * any deps loops, as can be a new object reference each render.
 */
export function normalizeCrop(cropData?: CropValues): CropValues {
  // for crop preview purposes
  // null and false can be treated the same
  if (cropData === false || cropData === null) {
    return null;
  }

  // verify crop object k:v valid, else null
  const isValidCrop = getIsValidCropObj(cropData);
  if (!isValidCrop) {
    return null;
  }

  /**
   * note: function intentionally returns a new object
   * here for consistent behavior, as this may be handling
   * both plain js objects as well as potentially proxied
   * objects from syncstore data. So, a new obj is returned
   * instead of just sending back the cropData arg directly.
   * Will need to useMemo the result if using in deps arrays.
   */
  const cropObj: ReqCropObj = {
    left: cropData?.left,
    top: cropData?.top,
    width: cropData?.width,
    height: cropData?.height,
  };

  return cropObj;
}

export type GetCroppedViaCanvasArgs = {
  baseImageUrl?: string;
  viewCrop?: CropValues;
  baseImageWidth?: number;
  baseImageHeight?: number;
};

/**
 * Returns object with data string property
 * - data url (data:image/jpeg) when crop applied
 * - or baseImageUrl when no crop defined
 */
type GetCroppedViaCanvasResult = {
  data: string;
};

/**
 * Get cropped data via Canvas API
 *
 * - baseImageUrl: source image URL (no crop/resize params)
 * - viewCrop: blox crop data obj
 * - baseImageWidth: source image width
 * - baseImageHeight: source image height
 *
 * Returns: `Promise<GetCroppedViaCanvasResult>`
 * eg: { data: string }
 * - data url (data:image/jpeg) when crop applied
 * - or baseImageUrl when no crop defined
 *
 * Throws error if invalid input or processing fails
 *
 * This is a directly awaitable approach to
 * generating a crop preview without needing to
 * rely on the ImageCrop (cropperjs/react-cropper)
 * components internal cropper ref. Does not require
 * the cropper ref to be mounted/active or relying on
 * any internal state. Uses Image, Canvas API to
 * load base image and draw the cropped portion,
 * returning the cropped area as a blob URL, or
 * the base image url passed if no crop defined.
 *
 */

export function getCroppedViaCanvas({
  baseImageUrl,
  viewCrop,
  baseImageWidth,
  baseImageHeight,
}: GetCroppedViaCanvasArgs): Promise<GetCroppedViaCanvasResult> {
  return new Promise((resolve, reject) => {
    // No image url to load - reject
    if (!baseImageUrl) {
      reject(new Error('[getCroppedViaCanvas] Missing base image url'));
      return;
    }

    // If crop is false/null/undefined,
    // there is effectively no crop,
    // resolve as base image url directly
    if (!viewCrop) {
      resolve({ data: baseImageUrl });
      return;
    }

    // validate crop values passed
    if (!getIsValidCropObj(viewCrop)) {
      reject(new Error('[getCroppedViaCanvas] Crop values invalid'));
      return;
    }

    // ensure width/height params exist
    if (!baseImageWidth || !baseImageHeight) {
      reject(new Error('[getCroppedViaCanvas] Image dimensions missing'));
      return;
    }

    // validate width/height values (no NaN, Inf, -Inf, etc)
    if (!isFiniteNumber(baseImageWidth) || !isFiniteNumber(baseImageHeight)) {
      reject(new Error('[getCroppedViaCanvas] Image dimensions invalid'));
      return;
    }

    // create image element for source
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        // setup canvas and context
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('[getCroppedViaCanvas] No canvas context available'));
          return;
        }

        // Calculate crop dimensions
        const cropX = viewCrop.left * baseImageWidth;
        const cropY = viewCrop.top * baseImageHeight;
        const cropWidth = viewCrop.width * baseImageWidth;
        const cropHeight = viewCrop.height * baseImageHeight;

        // Sanity check calculations
        const xValid = isFiniteNumber(cropX);
        const yValid = isFiniteNumber(cropY);
        const wValid = isFiniteNumber(cropWidth);
        const hValid = isFiniteNumber(cropHeight);

        if (!xValid || !yValid || !wValid || !hValid) {
          reject(new Error('[getCroppedViaCanvas] Calculated dimensions were invalid'));
          return;
        }

        // Set canvas size to crop size
        canvas.width = cropWidth;
        canvas.height = cropHeight;

        // Draw cropped portion
        ctx.drawImage(
          img,
          cropX,
          cropY,
          cropWidth,
          cropHeight, // source rectangle
          0,
          0,
          cropWidth,
          cropHeight, // destination rectangle
        );

        // Convert canvas to data URL jpeg
        // with lower quality for smaller size
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        // Cleanup temp elements
        canvas.remove();
        // Resolve with cropped data URL
        resolve({ data: dataUrl });
      } catch (error) {
        // Reject with error if something goes wrong
        reject(error);
      }
    };

    // Handle image load errors
    img.onerror = (event) => {
      reject(
        new Error(`[getCroppedViaCanvas] Failed to load image: ${baseImageUrl}`, {
          cause: event, // include event for more context
        }),
      );
    };

    // loading the image
    img.src = baseImageUrl;
  });
}

/**
 * Per MDN docs:
 * https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toDataURL
 * toDataURL() encodes the whole image in an in-memory string, which
 * may have performance implications, and may even overflow browsers
 * URL length limit when assigned to HTMLImageElement.src. They generally
 * recommend to use canvas.toBlob() in combination with URL.createObjectURL()
 *
 * If we need to adjust `getCroppedViaCanvas` to return blob URLs,
 * instead of data URLs we can modify it instead to resolve with:
 
 *  canvas.toBlob((blob) => {
 *    try {
 *      if (!blob) {
 *          canvas.remove();
 *          reject(new Error('[getCroppedViaCanvas] Failed to create blob'));
 *          return;
 *        }
 *        const blobUrl = URL.createObjectURL(blob);
 *        resolve({ data: blobUrl });
 *        canvas.remove();
 *      } catch (error) {
 *        canvas.remove();
 *        reject(error);
 *      }
 *    }, 'image/png');
 *  } catch (error) {
 *    // Reject with error if something goes wrong
 *    reject(error);
 *  }
 * 
 * However do note that other components like ImageCrop/Cropper.js 
 * still rely on toDataURL in some areas to extract cropped preview. 
 * For now keeping getCroppedViaCanvas as toDataURL for consistency.
 *
 */

type MaybeBlobUrl = string | null | undefined;

/**
 * Revokes a blob URL if the provided URL is a blob URL.
 * @param url - The URL string to potentially revoke.
 * @returns True if the URL was a blob URL and was revoked, false otherwise.
 */

export function tryRevokeBlobUrl(url?: MaybeBlobUrl): boolean {
  try {
    if (!url || typeof url !== 'string') {
      return false;
    }
    if (!url.startsWith('blob:')) {
      return false;
    }
    if (!window?.URL?.revokeObjectURL) {
      return false;
    }

    window?.URL?.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('[tryRevokeBlobUrl]:', error);
    return false;
  }
}
